import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function readRpc(response, label) {
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${responseText || response.statusText}`
    );
  }

  const result =
    responseText
      ? JSON.parse(responseText)
      : null;

  if (result?.ok === false) {
    throw new Error(
      result.message ||
      `${label} failed.`
    );
  }

  return result;
}

async function fetchCategories() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_use_categories?select=*,instructors(id,name,email)&order=display_order.asc,name.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(
      `Loading AI categories failed (${response.status})`
    );
  }

  return response.json();
}

async function fetchAiUseTypes() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_use_types?select=*,ai_use_categories(name),instructors(id,name,email)&order=label.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(
      `Loading AI use types failed (${response.status})`
    );
  }

  return response.json();
}

async function saveCategory(
  data,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/ai_template_create_category`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_name:
          data.name,
        p_display_order:
          Number(
            data.display_order
          )
      })
    }
  );

  return readRpc(
    response,
    'Creating AI category'
  );
}

async function updateCategory(
  id,
  data,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/ai_template_update_category`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_category_id:
          id,
        p_name:
          data.name,
        p_display_order:
          Number(
            data.display_order
          )
      })
    }
  );

  return readRpc(
    response,
    'Updating AI category'
  );
}

async function deleteCategory(
  id,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/ai_template_delete_category`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_category_id:
          id
      })
    }
  );

  return readRpc(
    response,
    'Deleting AI category'
  );
}

async function saveAiUseType(
  data,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/ai_template_create_type`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_label:
          data.label,
        p_description:
          data.description || null,
        p_category_id:
          data.category_id || null
      })
    }
  );

  return readRpc(
    response,
    'Creating AI use type'
  );
}

async function updateAiUseType(
  id,
  data,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/ai_template_update_type`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_type_id:
          id,
        p_label:
          data.label,
        p_description:
          data.description || null,
        p_category_id:
          data.category_id || null
      })
    }
  );

  return readRpc(
    response,
    'Updating AI use type'
  );
}

async function deleteAiUseType(
  id,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/ai_template_delete_type`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_type_id:
          id
      })
    }
  );

  return readRpc(
    response,
    'Deleting AI use type'
  );
}

export default function GeneralAiPermissions({ session }) {
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('types');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Category form state
  const [editingCategory, setEditingCategory] = useState(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryOrder, setNewCategoryOrder] = useState(0);

  // Type form state
  const [editingType, setEditingType] = useState(null);
  const [addingType, setAddingType] = useState(false);
  const [newType, setNewType] = useState({ label: '', description: '', category_id: '' });

  function canEdit(item) {
    return (
      session?.isSuperadmin === true ||
      (
        item?.instructor_id &&
        item.instructor_id ===
          session?.instructorId
      )
    );
  }

  function ownerLabel(item) {
    if (
      item?.instructor_id ===
      session?.instructorId
    ) {
      return 'Created by you';
    }

    if (item?.instructors?.name) {
      return `Created by ${item.instructors.name}`;
    }

    if (item?.instructor_id) {
      return 'Created by another instructor';
    }

    return 'Legacy template item — admin managed';
  }

  async function refreshTemplate() {
    const [
      nextCategories,
      nextTypes
    ] = await Promise.all([
      fetchCategories(),
      fetchAiUseTypes()
    ]);

    setCategories(
      Array.isArray(nextCategories)
        ? nextCategories
        : []
    );

    setTypes(
      Array.isArray(nextTypes)
        ? nextTypes
        : []
    );
  }

  useEffect(() => {
    Promise.all([fetchCategories(), fetchAiUseTypes()])
      .then(([c, t]) => { setCategories(c); setTypes(t); })
      .finally(() => setLoading(false));
  }, []);

  // ---- Category handlers ----
  async function handleAddCategory(e) {
    e.preventDefault();
    setError('');
    if (!newCategoryName.trim()) return setError('Enter a category name.');
    setSaving(true);
    try {
      await saveCategory(
        {
          name:
            newCategoryName.trim(),
          display_order:
            Number(
              newCategoryOrder
            )
        },
        session.cmsToken
      );

      await refreshTemplate();
      setNewCategoryName('');
      setNewCategoryOrder(0);
      setAddingCategory(false);
      setSuccess('Category added.');
    } catch (err) {
      setError('Failed to save category.');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateCategory(e) {
    e.preventDefault();
    setError('');
    if (!editingCategory.name.trim()) return setError('Enter a category name.');

    if (!canEdit(editingCategory)) {
      return setError(
        'Only the instructor who created this category can edit it.'
      );
    }

    setSaving(true);
    try {
      await updateCategory(
        editingCategory.id,
        {
          name:
            editingCategory.name.trim(),
          display_order:
            Number(
              editingCategory.display_order
            )
        },
        session.cmsToken
      );

      await refreshTemplate();
      setEditingCategory(null);
      setSuccess('Category updated.');
    } catch (err) {
      setError('Failed to update category.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCategory(cat) {
    if (!canEdit(cat)) {
      setError(
        'Only the instructor who created this category can delete it.'
      );
      return;
    }

    if (!window.confirm(`Delete "${cat.name}"? AI use types in this category will become uncategorised.`)) return;

    await deleteCategory(
      cat.id,
      session.cmsToken
    );

    await refreshTemplate();
    setSuccess('Category deleted.');
  }

  // ---- Type handlers ----
  async function handleAddType(e) {
    e.preventDefault();
    setError('');
    if (!newType.label.trim()) return setError('Enter a label.');
    setSaving(true);
    try {
      await saveAiUseType(
        {
          label:
            newType.label.trim(),
          description:
            newType.description.trim() ||
            null,
          category_id:
            newType.category_id ||
            null
        },
        session.cmsToken
      );

      await refreshTemplate();
      setNewType({ label: '', description: '', category_id: '' });
      setAddingType(false);
      setSuccess('AI use type added.');
    } catch (err) {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateType(e) {
    e.preventDefault();
    setError('');
    if (!editingType.label.trim()) return setError('Enter a label.');

    if (!canEdit(editingType)) {
      return setError(
        'Only the instructor who created this AI use type can edit it.'
      );
    }

    setSaving(true);
    try {
      await updateAiUseType(
        editingType.id,
        {
          label:
            editingType.label.trim(),
          description:
            editingType.description?.trim() ||
            null,
          category_id:
            editingType.category_id ||
            null
        },
        session.cmsToken
      );

      await refreshTemplate();
      setEditingType(null);
      setSuccess('Updated.');
    } catch (err) {
      setError('Failed to update.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteType(type) {
    if (!canEdit(type)) {
      setError(
        'Only the instructor who created this AI use type can delete it.'
      );
      return;
    }

    if (!window.confirm(`Delete "${type.label}"?`)) return;

    await deleteAiUseType(
      type.id,
      session.cmsToken
    );

    await refreshTemplate();
    setSuccess('Deleted.');
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  // Group types by category for display
  const typesByCategory = categories.map(cat => ({
    category: cat,
    types: types.filter(t => t.category_id === cat.id)
  }));
  const uncategorised = types.filter(t => !t.category_id);

  return (
    <div>
      <h2 style={styles.subheading}>AI Permissions</h2>
      <p style={styles.muted}>
        Define categories and individual AI use types. For each course, you select which
        types are permitted. Students see permitted types grouped by category.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.sectionTabs}>
        <button
          style={{ ...styles.sectionTab, ...(activeSection === 'types' ? styles.sectionTabActive : {}) }}
          onClick={() => setActiveSection('types')}
        >
          AI Use Types
        </button>
        <button
          style={{ ...styles.sectionTab, ...(activeSection === 'categories' ? styles.sectionTabActive : {}) }}
          onClick={() => setActiveSection('categories')}
        >
          Categories
        </button>
      </div>

      {activeSection === 'categories' && (
        <div>
          <p style={styles.muted}>
            Categories group AI use types. Set a display order to control how they appear to students.
          </p>

          <div style={styles.list}>
            {categories.map(cat => (
              <div key={cat.id} style={styles.item}>
                {editingCategory?.id === cat.id && canEdit(cat) ? (
                  <form onSubmit={handleUpdateCategory}>
                    <div style={styles.inlineRow}>
                      <div style={{ ...styles.field, flex: 3 }}>
                        <label style={styles.label}>Name</label>
                        <input
                          style={styles.input}
                          type="text"
                          value={editingCategory.name}
                          onChange={e => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
                          autoFocus
                        />
                      </div>
                      <div style={{ ...styles.field, flex: 1, minWidth: '80px' }}>
                        <label style={styles.label}>Order</label>
                        <input
                          style={styles.input}
                          type="number"
                          value={editingCategory.display_order}
                          onChange={e => setEditingCategory(prev => ({ ...prev, display_order: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div style={styles.btnRow}>
                      <button style={styles.btn} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                      <button type="button" style={styles.secondaryBtn} onClick={() => setEditingCategory(null)}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div style={styles.itemInner}>
                    <div style={styles.itemLeft}>
                      <div style={styles.itemLabel}>{cat.name}</div>
                      <div style={styles.itemMeta}>
                        Order: {cat.display_order} · {types.filter(t => t.category_id === cat.id).length} use types
                      </div>
                      <div style={styles.itemMeta}>
                        {ownerLabel(cat)}
                      </div>
                    </div>
                    <div style={styles.itemActions}>
                      {canEdit(cat) && (
                        <>
                          <button
                            style={styles.editBtn}
                            onClick={() => {
                              setEditingCategory({
                                ...cat
                              });
                              setAddingCategory(false);
                            }}
                          >
                            Edit
                          </button>

                          <button
                            style={styles.dangerBtn}
                            onClick={() =>
                              handleDeleteCategory(
                                cat
                              )
                            }
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {categories.length === 0 && <div style={styles.muted}>No categories yet.</div>}
          </div>

          {addingCategory ? (
            <form onSubmit={handleAddCategory} style={styles.addBox}>
              <h3 style={styles.addHeading}>Add Category</h3>
              <div style={styles.inlineRow}>
                <div style={{ ...styles.field, flex: 3 }}>
                  <label style={styles.label}>Name</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    placeholder="e.g. Writing Assistance"
                    autoFocus
                  />
                </div>
                <div style={{ ...styles.field, flex: 1, minWidth: '80px' }}>
                  <label style={styles.label}>Order</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={newCategoryOrder}
                    onChange={e => setNewCategoryOrder(e.target.value)}
                  />
                </div>
              </div>
              <div style={styles.btnRow}>
                <button style={styles.btn} disabled={saving}>{saving ? 'Adding...' : 'Add Category'}</button>
                <button type="button" style={styles.secondaryBtn} onClick={() => { setAddingCategory(false); setNewCategoryName(''); }}>Cancel</button>
              </div>
            </form>
          ) : (
            <button style={styles.addBtn} onClick={() => { setAddingCategory(true); setEditingCategory(null); }}>
              + Add Category
            </button>
          )}
        </div>
      )}

      {activeSection === 'types' && (
        <div>
          {/* Grouped by category */}
          {typesByCategory.map(({ category, types: catTypes }) => (
            <div key={category.id} style={styles.categoryGroup}>
              <div style={styles.categoryGroupHeading}>{category.name}</div>
              {catTypes.map(type => (
                <TypeItem
                  key={type.id}
                  type={type}
                  categories={categories}
                  editing={editingType?.id === type.id ? editingType : null}
                  setEditing={setEditingType}
                  onUpdate={handleUpdateType}
                  onDelete={handleDeleteType}
                  saving={saving}
                  canEdit={canEdit(type)}
                  ownerLabel={ownerLabel(type)}
                  styles={styles}
                />
              ))}
              {catTypes.length === 0 && (
                <div style={{ ...styles.muted, paddingLeft: '12px' }}>No types in this category.</div>
              )}
            </div>
          ))}

          {/* Uncategorised */}
          {uncategorised.length > 0 && (
            <div style={styles.categoryGroup}>
              <div style={{ ...styles.categoryGroupHeading, color: '#66788a' }}>Uncategorised</div>
              {uncategorised.map(type => (
                <TypeItem
                  key={type.id}
                  type={type}
                  categories={categories}
                  editing={editingType?.id === type.id ? editingType : null}
                  setEditing={setEditingType}
                  onUpdate={handleUpdateType}
                  onDelete={handleDeleteType}
                  saving={saving}
                  canEdit={canEdit(type)}
                  ownerLabel={ownerLabel(type)}
                  styles={styles}
                />
              ))}
            </div>
          )}

          {types.length === 0 && <div style={styles.muted}>No AI use types defined yet.</div>}

          {addingType ? (
            <form onSubmit={handleAddType} style={styles.addBox}>
              <h3 style={styles.addHeading}>Add AI Use Type</h3>
              <div style={styles.field}>
                <label style={styles.label}>Label</label>
                <input
                  style={styles.input}
                  type="text"
                  value={newType.label}
                  onChange={e => setNewType(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="e.g. Grammar checking"
                  autoFocus
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Category</label>
                <select
                  style={styles.input}
                  value={newType.category_id}
                  onChange={e => setNewType(prev => ({ ...prev, category_id: e.target.value }))}
                >
                  <option value="">— Uncategorised —</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Full description</label>
                <textarea
                  style={styles.textarea}
                  value={newType.description}
                  onChange={e => setNewType(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Explain what this AI use category means..."
                />
              </div>
              <div style={styles.btnRow}>
                <button style={styles.btn} disabled={saving}>{saving ? 'Adding...' : 'Add'}</button>
                <button type="button" style={styles.secondaryBtn} onClick={() => { setAddingType(false); setNewType({ label: '', description: '', category_id: '' }); }}>Cancel</button>
              </div>
            </form>
          ) : (
            <button style={styles.addBtn} onClick={() => { setAddingType(true); setEditingType(null); }}>
              + Add AI Use Type
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TypeItem({
  type,
  categories,
  editing,
  setEditing,
  onUpdate,
  onDelete,
  saving,
  canEdit,
  ownerLabel,
  styles
}) {
  return (
    <div style={styles.item}>
      {editing && canEdit ? (
        <form onSubmit={onUpdate}>
          <div style={styles.field}>
            <label style={styles.label}>Label</label>
            <input
              style={styles.input}
              type="text"
              value={editing.label}
              onChange={e => setEditing(prev => ({ ...prev, label: e.target.value }))}
              autoFocus
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Category</label>
            <select
              style={styles.input}
              value={editing.category_id || ''}
              onChange={e => setEditing(prev => ({ ...prev, category_id: e.target.value }))}
            >
              <option value="">— Uncategorised —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Full description</label>
            <textarea
              style={styles.textarea}
              value={editing.description || ''}
              onChange={e => setEditing(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>
          <div style={styles.btnRow}>
            <button style={styles.btn} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" style={styles.secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      ) : (
        <div style={styles.itemInner}>
          <div style={styles.itemLeft}>
            <div style={styles.itemLabel}>{type.label}</div>
            {type.description && <div style={styles.itemDesc}>{type.description}</div>}
            <div style={styles.itemMeta}>
              {ownerLabel}
            </div>
          </div>
          <div style={styles.itemActions}>
            {canEdit && (
              <>
                <button
                  style={styles.editBtn}
                  onClick={() =>
                    setEditing({
                      ...type
                    })
                  }
                >
                  Edit
                </button>

                <button
                  style={styles.dangerBtn}
                  onClick={() =>
                    onDelete(type)
                  }
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 12px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  sectionTabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  sectionTab: {
    border: '1px solid #d7dee5', background: 'white', borderRadius: '8px',
    padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#66788a'
  },
  sectionTabActive: { background: '#215b7d', borderColor: '#215b7d', color: 'white' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  categoryGroup: { marginBottom: '20px' },
  categoryGroupHeading: {
    fontSize: '13px', fontWeight: '700', color: '#215b7d',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #d7dee5'
  },
  item: {
    border: '1px solid #d7dee5', borderRadius: '10px',
    padding: '12px 14px', background: 'white', marginBottom: '6px'
  },
  itemInner: { display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' },
  itemLeft: { flex: 1, minWidth: 0 },
  itemLabel: { fontWeight: '700', fontSize: '14px', color: '#1f2933', marginBottom: '4px' },
  itemDesc: { fontSize: '13px', color: '#66788a', lineHeight: '1.4' },
  itemMeta: { fontSize: '12px', color: '#66788a', marginTop: '2px' },
  itemActions: { display: 'flex', gap: '8px', flexShrink: 0 },
  inlineRow: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  field: { marginBottom: '12px', flex: 1 },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  textarea: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box',
    background: 'white', resize: 'vertical'
  },
  btnRow: { display: 'flex', gap: '10px' },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'transparent', color: '#66788a', border: '1px solid #d7dee5',
    borderRadius: '8px', padding: '9px 16px', fontSize: '14px', cursor: 'pointer'
  },
  editBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  addBox: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '20px',
    background: '#f7f9fa', marginTop: '8px'
  },
  addHeading: { margin: '0 0 16px', fontSize: '16px', color: '#1f2933' },
  addBtn: {
    background: 'transparent', color: '#215b7d', border: '2px dashed #b0cfe0',
    borderRadius: '10px', padding: '12px 20px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', width: '100%', marginTop: '4px'
  }
};